import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Listbox, Transition } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { Fragment } from 'react';
import { SecondaryButton, DefaultButton } from '@/components/ui/Button';

interface UpdateRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedRole: string) => void;
  currentRole?: string;
}

const roles = [
  { label: 'Member', value: 'Member' },
  { label: 'Admin SOP', value: 'Admin SOP' },
  { label: 'Owner', value: 'Owner' },
];

export default function UpdateRoleModal({ open, onClose, onSubmit, currentRole }: Readonly<UpdateRoleModalProps>) {
  const [selectedRole, setSelectedRole] = useState(currentRole || roles[0].value);

  const handleSubmit = () => {
    onSubmit(selectedRole);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white rounded-lg p-6 max-w-sm w-full">
          <DialogTitle className="text-lg font-semibold mb-4">Update Member Role</DialogTitle>

          <div className="mb-6">
            <Listbox value={selectedRole} onChange={setSelectedRole}>
              <div className="relative">
                <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm">
                  <span className="block truncate">{selectedRole}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDown size={16} />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    {roles.map(role => (
                      <Listbox.Option
                        key={role.value}
                        value={role.value}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {role.label}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <Check size={16} />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <DefaultButton
              onClick={handleSubmit}
              className="!bg-blue-600 hover:!bg-blue-700 text-white rounded-lg px-3 py-1 text-sm"
            >
              Save Changes
            </DefaultButton>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
